import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError } from "../lib/api";
import { fetchSharedFrise, queryKeys } from "./api";
import { todayIso } from "./dates";
import { Frise } from "./frise";
import { datedInOrder, undatedEntries } from "./frise-layout";
import { UndatedTray } from "./undated-tray";

/**
 * La page d'un lien de partage (`/v/:token` ; #588) : la frise en lecture seule, **sans ecran de
 * connexion**, reduite par le serveur aux pistes que le proprietaire a ouvertes. Pas de navigation
 * de l'app, pas de « Proposer », pas de reglages : la personne n'a pas de compte et ne peut rien
 * ecrire. Le serveur n'a rien envoye d'autre que ce qui est montre ici.
 *
 * Un lien inconnu, mal forme ou revoque rend le meme message neutre.
 */

/** Pose `robots` et `referrer` le temps de la page : l'adresse porte le jeton, elle ne se repand pas. */
function useDiscreetHead(): void {
  useEffect(() => {
    const added = [
      ["robots", "noindex, nofollow"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const meta = document.createElement("meta");
      meta.name = name as string;
      meta.content = content as string;
      document.head.append(meta);
      return meta;
    });
    return () => {
      for (const meta of added) meta.remove();
    };
  }, []);
}

export function SharedPage({ today = todayIso() }: { today?: string } = {}) {
  const { token } = useParams({ strict: false });
  useDiscreetHead();
  const frise = useQuery({
    queryKey: queryKeys.sharedFrise(token ?? ""),
    queryFn: () => fetchSharedFrise(token ?? ""),
    enabled: token !== undefined,
    retry: false,
    // Une lecture suffit : chaque ouverture est comptee par le serveur.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });

  if (frise.isPending && token !== undefined) {
    return (
      <p className="text-muted-foreground" role="status">
        Chargement...
      </p>
    );
  }
  if (token === undefined || frise.isError) {
    const gone =
      token === undefined || (frise.error instanceof ApiError && frise.error.status === 404);
    return gone ? (
      <section className="flex flex-col gap-2 py-6">
        <h1 className="tl-display m-0 text-[28px]">Ce lien n'existe plus</h1>
        <p className="m-0 text-base text-[#C9D0DE]" role="alert">
          Il a été révoqué, ou l'adresse est incomplète. Demande à la personne qui te l'a envoyé de
          t'en donner un nouveau.
        </p>
      </section>
    ) : (
      <section className="flex flex-col items-start gap-3 py-6">
        <p className="m-0 text-destructive" role="alert">
          La frise ne répond pas pour le moment. Réessaie dans un instant.
        </p>
        <button
          className="min-h-11 rounded-[10px] border border-input px-4 text-sm"
          onClick={() => void frise.refetch()}
          type="button"
        >
          Réessayer
        </button>
      </section>
    );
  }

  const data = frise.data;
  if (data === undefined) return null;
  const dated = datedInOrder(data.entries);
  const undated = undatedEntries(data.entries);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="tl-display m-0 text-[30px] md:text-[44px]">
          {data.ownerName === null ? "Une vie, " : `La vie de ${data.ownerName}, `}
          <em className="text-tl-accent italic">en étoiles</em>
        </h1>
        <p className="m-0 text-sm text-muted-foreground">
          On a partagé avec toi une partie de cette frise, en lecture seule.
        </p>
      </div>

      {dated.length > 0 ? (
        <Frise birthDate={null} entries={data.entries} today={today} tracks={data.tracks} />
      ) : (
        <div className="flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-5">
          <p className="tl-display m-0 text-2xl">Rien à voir ici pour le moment.</p>
          <p className="m-0 text-sm text-muted-foreground">
            Aucun moment n'est ouvert sur ce lien. Repasse plus tard.
          </p>
        </div>
      )}

      <UndatedTray entries={undated} tracks={data.tracks} />
    </div>
  );
}
